import { ChangeEvent } from "react";
import { Dayjs } from "dayjs";
import "dayjs/locale/th";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { PricebookApplyMode } from "../../../../types/price-book.type";
import { todayInBangkok } from "../../../../utils/date";
import {
  APPLY_MODES,
  APPLY_MODE_HINT,
  APPLY_MODE_LABEL,
  ApplyTimingErrors,
  ApplyTimingForm,
  toApplyMode,
} from "./applyTiming";

const PICKER_FORMAT = "DD/MM/YYYY";

interface ApplyTimingFieldsProps {
  form: ApplyTimingForm;
  errors: ApplyTimingErrors;
  /** The API's reason the draft cannot go as a range; null when it can. */
  rangeBlockReason: string | null;
  disabled: boolean;
  onChange: (changes: Partial<ApplyTimingForm>) => void;
}

/**
 * When the draft's prices reach its source: at once, from a later day, or only between two days
 * (shut, with the API's reason, while the draft changes more than prices).
 */
export default function ApplyTimingFields({
  form,
  errors,
  rangeBlockReason,
  disabled,
  onChange,
}: ApplyTimingFieldsProps) {
  const today = todayInBangkok();
  // A scheduled day must come after today; a range may start today.
  const firstScheduleDay = today.add(1, "day");
  const endMinDate =
    form.startDate?.isValid() && form.startDate.isAfter(today, "day")
      ? form.startDate
      : today;
  const isModeDisabled = (mode: PricebookApplyMode): boolean =>
    disabled || (mode === "range" && Boolean(rangeBlockReason));

  const handleChangeMode = (_event: ChangeEvent<HTMLInputElement>, value: string) => {
    const mode = toApplyMode(value);
    if (mode) onChange({ mode });
  };
  const handleChangeEffectiveDate = (effectiveDate: Dayjs | null) =>
    onChange({ effectiveDate });
  const handleChangeStartDate = (startDate: Dayjs | null) => onChange({ startDate });
  const handleChangeEndDate = (endDate: Dayjs | null) => onChange({ endDate });

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
      <Stack gap={1.5}>
        <Typography variant="body2" fontWeight={600}>
          เวลาที่มีผล
        </Typography>
        <RadioGroup value={form.mode} onChange={handleChangeMode}>
          {APPLY_MODES.map((mode) => (
            <FormControlLabel
              key={mode}
              value={mode}
              control={<Radio size="small" />}
              disabled={isModeDisabled(mode)}
              label={<ModeLabel mode={mode} rangeBlockReason={rangeBlockReason} />}
            />
          ))}
        </RadioGroup>
        {errors.mode && (
          <Typography variant="caption" color="error">
            {errors.mode}
          </Typography>
        )}
        {form.mode === "date" && (
          <DatePicker
            label="วันเริ่มใช้"
            value={form.effectiveDate}
            format={PICKER_FORMAT}
            minDate={firstScheduleDay}
            disabled={disabled}
            onChange={handleChangeEffectiveDate}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                error: Boolean(errors.effectiveDate),
                helperText: errors.effectiveDate,
              },
            }}
          />
        )}
        {form.mode === "range" && (
          <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
            <DatePicker
              label="วันเริ่ม"
              value={form.startDate}
              format={PICKER_FORMAT}
              minDate={today}
              disabled={disabled}
              onChange={handleChangeStartDate}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  error: Boolean(errors.startDate),
                  helperText: errors.startDate,
                },
              }}
            />
            <DatePicker
              label="วันสิ้นสุด"
              value={form.endDate}
              format={PICKER_FORMAT}
              minDate={endMinDate}
              disabled={disabled}
              onChange={handleChangeEndDate}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  error: Boolean(errors.endDate),
                  helperText: errors.endDate,
                },
              }}
            />
          </Stack>
        )}
      </Stack>
    </LocalizationProvider>
  );
}

interface ModeLabelProps {
  mode: PricebookApplyMode;
  rangeBlockReason: string | null;
}

/** The mode's name over what it does — or, for a range the draft cannot take, why not. */
function ModeLabel({ mode, rangeBlockReason }: ModeLabelProps) {
  const blockReason = mode === "range" ? rangeBlockReason : null;

  return (
    <Stack sx={{ py: 0.5 }}>
      <Typography variant="body2">{APPLY_MODE_LABEL[mode]}</Typography>
      <Typography variant="caption" color={blockReason ? "warning.main" : "text.secondary"}>
        {blockReason ?? APPLY_MODE_HINT[mode]}
      </Typography>
    </Stack>
  );
}
